/* global SGViewState, $ */
import Broadcast from './SGEventBroadcast';
import Logger from '../../common/debug/tagged-logger';

let instance;

/**
 * SGEvent
 */
class SGEvent {
  /**
   * Init stuff
   */
  constructor() {
    this.callbacks = {};
    this.garbageCollectionInterval = 5000;
    this.garbageCollectionReleaseThreshold = 6000;
    this.garbageCollectionHandle = null;
    this.logger = new Logger('SGEvent');
    this.Broadcast = new Broadcast();
    this.Broadcast.declareAll();

    this.getEventNameRegExp = this.getEventNameRegExp.bind(this);
  }

  /**
   * Call an SGEvent and trigger a js event with the same name for the window obj.
   * Is used by the app to deliver all events into the current view.
   * @param {string} eventName - The name of the event that should be called
   * @param {Array} _eventArguments - The arguments for the related event callbacks
   */
  __call(eventName, _eventArguments = []) {
    let eventArguments = _eventArguments;
    // Guarantee that there is an array in the eventArguments
    if (!eventArguments || !Array.isArray(eventArguments)) {
      eventArguments = [];
    }

    this.logger.info('Event: %s | arguments:', eventName, eventArguments || []);

    let called = false;

    if (eventName === 'pageInsetsChanged') {
      SGViewState.insets = eventArguments[0];
    }

    if (this[eventName]) {
      // Directly call the event
      this[eventName].apply(this, eventArguments);

      // Also trigger a js event
      $(window).trigger(eventName, eventArguments);

      called = true;
    }

    const callbackNames = Object.keys(this.callbacks);

    // Invoke the callbacks registered by the webviews
    if (callbackNames.length) {
      // Get an event name RegExp object
      const eventNameRegExp = this.getEventNameRegExp(eventName);

      callbackNames.forEach((currentEventName) => {
        // Reset the RegExp object
        eventNameRegExp.lastIndex = 0;

        // Check if the current callback list matches the event name
        if (
          eventNameRegExp.test(currentEventName) && Array.isArray(this.callbacks[currentEventName])
        ) {
          // Invoke the callback functions
          for (
            let callbackIndex = 0;
            callbackIndex < this.callbacks[currentEventName].length;
            callbackIndex += 1
          ) {
            const callback = this.callbacks[currentEventName][callbackIndex];

            // Only invoke the callback if it's allowed
            if (callback.executable) {
              callback.fn.apply(this, eventArguments);
              called = true;
            }
          }
        }
      });
    }

    if (!called) {
      this.logger.warn(`Attempt to call unknown event: ${eventName}`);
    }
  }

  /**
   * Registers a listener for a single, or a group of events
   * @param  {string} _eventNames - One event name, or a list of comma separated event names
   * @param  {Function} callback - The callback function
   * @param  {boolean} [ignoreGlobalCallback] If set to true the global callback
   * - located in the lib - for the event will be removed
   * @return {SGEvent} - The SGEvent object to enable method chaining
   */
  registerCallback(_eventNames, callback, ignoreGlobalCallback) {
    const eventNames = _eventNames.split(',');

    eventNames.forEach((eventName) => {
      const currentEventName = eventName.trim();

      if (!currentEventName) {
        return;
      }

      // Initialize an array to store the callback for the current event if not already present
      if (!Array.isArray(this.callbacks[currentEventName])) {
        this.callbacks[currentEventName] = [];
      }

      // Take care that only functions are added to the callback list
      if (typeof callback === 'function') {
        this.callbacks[currentEventName].push({
          fn: callback,
          executable: true,
          ts: null,
        });
      }

      // Remove the "global" callback if requested
      // (the one that is directly added in the LibShopgate)
      if (ignoreGlobalCallback && this[currentEventName]) {
        this[currentEventName] = function () {
        };
      }
    });

    return this;
  }

  /**
   * Removes listeners for a single, or a group of events (namespaced listeners are also accepted)
   * @param  {string} _eventNames - One event name, or a list of comma separated event names
   * @param  {boolean} unregisterNamespaces - If set to TRUE also the
   * namespaced listeners will be removed
   * @return {SGEvent} The SGEvent object to enable method chaining
   */
  unregisterCallback(_eventNames, unregisterNamespaces) {
    const eventNames = _eventNames.split(',');

    eventNames.forEach((eventName) => {
      const currentEventName = eventName.trim();

      if (!currentEventName) {
        return;
      }

      const eventCallbacksNames = Object.keys(this.callbacks);

      if (eventCallbacksNames.length) {
        // Get an event name RegExp object
        const eventNameRegExp = this.getEventNameRegExp(currentEventName);

        eventCallbacksNames.forEach((eventNameInCallbacks) => {
          // Reset the RegExp object
          eventNameRegExp.lastIndex = 0;

          const matches = eventNameRegExp.exec(eventNameInCallbacks);

          // The current callback list could be a candidate to be removed
          if (matches) {
            const matchedEventName = matches[1];
            const matchedEventNamespace = matches[2];
            let deactivateCallbacks = false;

            if (matchedEventName) {
              if (matchedEventNamespace) {
                /*
                 * Namespaced callback list for the requested event name found.
                 * These are only removed if explicitly requested
                 */
                if (unregisterNamespaces === true) {
                  deactivateCallbacks = true;
                }
              } else {
                // No namespace found, so it's the exact callback list for the requested event name
                deactivateCallbacks = true;
              }
            }

            /*
             * Deactivate the related callbacks. simply removing the functions is not an option
             * since it's not possible to remove callbacks while they are executed. normally
             * the callbacks will be unregistered within the callback itself
             */
            if (deactivateCallbacks) {
              for (
                let callbackIndex = 0;
                callbackIndex < this.callbacks[eventNameInCallbacks].length;
                callbackIndex += 1
              ) {
                this.callbacks[eventNameInCallbacks][callbackIndex].executable = false;
                this.callbacks[eventNameInCallbacks][callbackIndex].ts = new Date().getTime();
              }

              /*
               Start the garbage collection if the unregisterCallback
               function is used for the fist time.
               So it's only active when the current webview seems to need it
               */
              this.initGarbageCollection();
            }
          }
        });
      }
    });

    return this;
  }

  /**
   * Logs a list of all available events to the console and counts the registered callbacks
   */
  info() {
    let info = '';

    Object.keys(this.callbacks).forEach((eventNameInCallbacks) => {
      info += `${eventNameInCallbacks} | Callbacks: ${this.callbacks[eventNameInCallbacks].length} \n`;
    });

    this.logger.info(info);
  }

  /**
   * Initializes the garbage collection
   */
  initGarbageCollection() {
    // Only start if it's not already started
    if (this.garbageCollectionHandle === null) {
      this.garbageCollectionHandle = setInterval(() => {
        this.garbageCollection();
      }, this.garbageCollectionInterval);
    }
  }

  /**
   * Initializes regular expression object with an event name
   * @param {string} eventName The event name
   * @return {RegExp} - RegExp for eventNames
   */
  getEventNameRegExp(eventName) {
    /*
     Regex to find callbacks for triggered events - it regards namespaced callback names
     (eventName.namespace)
     Webviews can register non namespaced events listeners like "mainViewWillAppear"
     or namespace event listeners like "mainViewWillAppear.namespace".
     If the app sends the "mainViewWillAppear" event,
     the callbacks for both event listeners will be invoked.
     The basic idea behind namespaced event listeners is to be able to create temporary
     listeners for common events that can be removed after usage,
     without influencing the other, static event listeners.

     If a match was found for the current pattern the matches array will look like:
     matches[
     'the complete match',
     'the basic event name',
     'the event name namespace - if present'
     ]

     Hint: Basic events names can contain as many periods (.) as requested.
     If an event is declared like "my.super.fine.event" it is of course possible to add a namespaced
     listener for it, like "my.super.fine.event.namespace"
     */

    return new RegExp(`^(${eventName}){1}(?:(?:\\.)([^\\.]+))?$`, 'i');
  }

  /**
   * The actually garbage collection
   * @param {boolean} leaveEventsWithoutCallbacks -
   */
  garbageCollection(leaveEventsWithoutCallbacks) {
    const currentTs = new Date().getTime();
    // Run through all events and the related callbacks to find deactivated callbacks.
    Object.keys(this.callbacks).forEach((eventNameInCallbacks) => {
      const callbacksForEvent = this.callbacks[eventNameInCallbacks];
      for (let callbackIndex = 0; callbackIndex < callbacksForEvent.length; callbackIndex += 1) {
        const currentCallback = callbacksForEvent[callbackIndex];
        // Remove a callback if complies with the release threshold parameter
        if (
          currentCallback.executable === false
          && ((currentTs - currentCallback.ts) > this.garbageCollectionReleaseThreshold)
        ) {
          callbacksForEvent.splice(callbackIndex, 1);
        }
      }

      // Remove the event if not prohibited
      if (leaveEventsWithoutCallbacks !== true && callbacksForEvent.length === 0) {
        delete this.callbacks[eventNameInCallbacks];
      }
    });
  }
}

/**
 * Returns singleton instance of SGEvent function
 * @param {boolean} forceNew - forces new instance
 * @return {*}
 */
function getInstance(forceNew = false) {
  if (!instance || forceNew) {
    instance = new SGEvent();
  }

  return instance;
}

export default getInstance;



// WEBPACK FOOTER //
// ./Library/1.0/app/sg-event/SGEvent.js